import React from 'lib/react';
import PropTypes from 'prop-types';
import { EVGrid, makeStyles } from 'lib/components';
import AnalyticsBarChart from 'commonComponents/analyticsBarChart';
import {
  getCurrentMonthName,
  padHistoricUsageTo12Months,
  PRODUCT_COLORS,
  formatChartValue,
  formatMonthName,
} from '../Consumption.utils';
import HistoricUsageTooltip from './HistoricUsageTooltip';

const useStyles = makeStyles((theme) => ({
  historicUsageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.evPalette.salt[100],
  },
  barChartDiv: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '320px',
    borderLeft: `1px solid ${theme.evPalette.charcoal[400]}`,
    borderRight: `1px solid ${theme.evPalette.charcoal[400]}`,
    borderBottom: `1px solid ${theme.evPalette.charcoal[400]}`,
    borderRadius: '4px',
    '& > *': {
      flex: 1,
      minHeight: 0,
    },
  },
}));

// Grey diagonal stripes overlaid on solid colors
function InProcessPattern({ id, color }) {
  return (
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
        <rect width="8" height="8" fill={color} />
        <rect width="4" height="8" fill="#b2c0d6" />
      </pattern>
    </defs>
  );
}

InProcessPattern.propTypes = {
  id: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

function NonSubscriptionHistoricUsage({ historicUsage, products }) {
  const classes = useStyles();
  const currentMonth = getCurrentMonthName();

  const paddedData = padHistoricUsageTo12Months(historicUsage);
  const hasData = historicUsage && historicUsage.length > 0;
  const transformedData = paddedData.map((item) => {
    const isCurrentMonth = item.month === currentMonth;
    const isNotStarted = item.notStarted || false;
    const transformed = { month: item.month };

    // Not started months: show small grey bar only if we have actual data
    transformed.notStartedBar = (hasData && isNotStarted) ? 0.1 : 0;

    products.forEach((product) => {
      const productId = String(product.id);
      const value = item[productId] || 0;
      // Past months: show in regular series (solid colors)
      transformed[productId] = (isCurrentMonth || isNotStarted) ? 0 : value;
      // Current month: show in in-process series (striped patterns)
      transformed[`${productId}_inprocess`] = isCurrentMonth ? value : 0;
    });

    return transformed;
  });

  const notStartedSeries = {
    dataKey: 'notStartedBar',
    color: '#D1D5DB',
    label: 'Not Started',
    valueFormatter: () => null,
    stack: 'products',
  };

  const regularSeries = products.map((product, index) => ({
    dataKey: String(product.id),
    color: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
    label: product.name,
    valueFormatter: formatChartValue,
    stack: 'products',
  }));

  const inProcessSeries = products.map((product) => ({
    dataKey: `${product.id}_inprocess`,
    color: `url(#inprocess-${product.id})`,
    label: product.name,
    valueFormatter: formatChartValue,
    stack: 'products',
  }));

  const allSeries = [notStartedSeries, ...regularSeries, ...inProcessSeries];

  return (
    <EVGrid className={classes.historicUsageContainer}>
      <EVGrid className={classes.barChartDiv}>
        <AnalyticsBarChart
          dataset={transformedData}
          series={allSeries}
          margin={{
            top: 0, right: 0, bottom: 24, left: 0,
          }}
          sx={{
            width: '100%',
            height: '100%',
            '& .MuiChartsGrid-lineVertical:not(:first-of-type):not(:last-of-type)': {
              stroke: 'none',
            },
            '& .MuiChartsAxis-root': {
              stroke: 'none',
            },
            '& .MuiChartsGrid-line': {
              stroke: '#B4BAC7',
            },
          }}
          tooltip={{ trigger: 'axis' }}
          slots={{
            axisContent: () => null,
          }}
          xAxis={[{
            scaleType: 'band',
            dataKey: 'month',
            categoryGapRatio: 0.2,
            disableTicks: true,
            valueFormatter: formatMonthName,
          }]}
          bargapratio={0}
          leftAxis={null}
          grid={{ vertical: false }}
        >
          {products.map((product, index) => (
            <InProcessPattern
              key={product.id}
              id={`inprocess-${product.id}`}
              color={PRODUCT_COLORS[index % PRODUCT_COLORS.length]}
            />
          ))}
          <HistoricUsageTooltip series={allSeries} />
        </AnalyticsBarChart>
      </EVGrid>
    </EVGrid>
  );
}

NonSubscriptionHistoricUsage.propTypes = {
  historicUsage: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.string.isRequired,
  })).isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
};

export default NonSubscriptionHistoricUsage;
